/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDProgress from "components/MDProgress";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";

function KinerjaTugas() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [kinerjaList, setKinerjaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [tahun, setTahun] = useState(null);
  const [bulan, setBulan] = useState(null);
  const [statistik, setStatistik] = useState({
    total_instruksi: 0,
    total_penerima: 0,
    total_selesai: 0,
    total_proses: 0,
    total_belum_dibaca: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
    } else {
      logoutUser(navigate);
    }
    // Set tahun dan bulan saat ini
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    setTahun(currentYear);
    setBulan(currentMonth);
  }, []);

  useEffect(() => {
    if (userId && tahun) {
      if (!bulan) {
        const currentMonth = new Date().getMonth() + 1;
        setBulan(currentMonth);
        return;
      }
      fetchKinerja();
      fetchStatistik();
    }
  }, [userId, tahun, bulan]);

  const fetchKinerja = async () => {
    try {
      setLoading(true);
      const bulanParam =
        bulan && bulan !== "semua" && bulan !== "0" ? `&bulan=${bulan}` : "";
      const response = await axios.get(
        `${API_ESA_OFFICE}instruksi/kinerja/${userId}?tahun=${tahun}${bulanParam}`
      );
      setKinerjaList(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching kinerja:", error);
      setLoading(false);
    }
  };

  const fetchStatistik = async () => {
    try {
      const bulanParam =
        bulan && bulan !== "semua" && bulan !== "0" ? `&bulan=${bulan}` : "";
      const response = await axios.get(
        `${API_ESA_OFFICE}instruksi/kinerja/${userId}/statistik?tahun=${tahun}${bulanParam}`
      );
      setStatistik(response.data);
    } catch (error) {
      console.error("Error fetching statistik:", error);
    }
  };

  const formatProfilUrl = (profil, uid_RSS) => {
    if (uid_RSS) {
      return `https://smartdh.rotendaokab.go.id/uploads/${uid_RSS}.jpg`;
    }
    if (profil) {
      return `${API_ESA_OFFICE_UPLOAD}${profil}`;
    }
    return null;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Kinerja Tugas
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun dan Bulan */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setTahun(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={[
                        { label: "Semua Bulan", value: "semua" },
                        { label: "Januari", value: "1" },
                        { label: "Februari", value: "2" },
                        { label: "Maret", value: "3" },
                        { label: "April", value: "4" },
                        { label: "Mei", value: "5" },
                        { label: "Juni", value: "6" },
                        { label: "Juli", value: "7" },
                        { label: "Agustus", value: "8" },
                        { label: "September", value: "9" },
                        { label: "Oktober", value: "10" },
                        { label: "November", value: "11" },
                        { label: "Desember", value: "12" },
                      ]}
                      value={
                        bulan
                          ? bulan === "semua"
                            ? { label: "Semua Bulan", value: "semua" }
                            : {
                                label: [
                                  "Januari",
                                  "Februari",
                                  "Maret",
                                  "April",
                                  "Mei",
                                  "Juni",
                                  "Juli",
                                  "Agustus",
                                  "September",
                                  "Oktober",
                                  "November",
                                  "Desember",
                                ][parseInt(bulan) - 1],
                                value: bulan,
                              }
                          : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setBulan(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Bulan"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  {/* Statistik */}
                  <Grid item xs={12} sm={12} md={6}>
                    <MDBox
                      display="flex"
                      gap={2}
                      flexWrap="wrap"
                      alignItems="center"
                      height="100%"
                    >
                      <Chip
                        label={`Total Instruksi: ${
                          statistik.total_instruksi || 0
                        }`}
                        color="info"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                      <Chip
                        label={`Total Penerima: ${
                          statistik.total_penerima || 0
                        }`}
                        color="primary"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                      <Chip
                        label={`Selesai: ${statistik.total_selesai || 0}`}
                        color="success"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                      <Chip
                        label={`Proses: ${statistik.total_proses || 0}`}
                        color="warning"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                      <Chip
                        label={`Belum Dibaca: ${
                          statistik.total_belum_dibaca || 0
                        }`}
                        color="error"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                    </MDBox>
                  </Grid>
                </Grid>

                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={4}
                  >
                    <MDTypography variant="body2" color="text.secondary">
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={2} px={2}>
                    {kinerjaList.map((item, index) => (
                      <Card key={item.penerima_id || index}>
                        <CardContent>
                          <MDBox mb={1}>
                            <MDBox display="flex" alignItems="center" gap={1.5}>
                              <MDAvatar
                                src={formatProfilUrl(
                                  item.profil_penerima,
                                  item.uid_RSS
                                )}
                                alt={item.nama_penerima}
                                size="sm"
                              />
                              <MDBox>
                                <MDTypography variant="h6" fontWeight="bold">
                                  {item.nama_penerima}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Total Instruksi:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {item.total_instruksi}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              Progress Selesai:
                            </MDTypography>
                            <MDProgress
                              value={item.persentase_selesai}
                              color="success"
                              variant="gradient"
                              sx={{ mb: 1 }}
                            />
                            <MDTypography variant="caption" color="text">
                              {item.persentase_selesai}%
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Selesai:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {item.total_selesai}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Proses:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {item.total_proses}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Belum Dibaca:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {item.total_belum_dibaca}
                            </MDTypography>
                          </MDBox>
                          {/* Rata-rata Rating */}
                          {item.rata_rata_nilai !== null && (
                            <MDBox mb={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                                mb={0.5}
                              >
                                Rata-rata Rating:
                              </MDTypography>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                              >
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Icon
                                    key={star}
                                    sx={{
                                      color:
                                        star <= Math.round(item.rata_rata_nilai)
                                          ? "#FFD700"
                                          : "#E0E0E0",
                                      fontSize: "1.2rem",
                                    }}
                                  >
                                    star
                                  </Icon>
                                ))}
                                <MDTypography
                                  variant="body2"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  ({item.rata_rata_nilai.toFixed(2)}/5)
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          )}
                          {/* Respon */}
                          {item.total_respon > 0 && (
                            <MDBox mb={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                                mb={0.5}
                              >
                                Respon:
                              </MDTypography>
                              <MDBox display="flex" gap={0.5} flexWrap="wrap">
                                {item.total_terima > 0 && (
                                  <Chip
                                    label={`Terima: ${item.total_terima}`}
                                    color="success"
                                    size="small"
                                  />
                                )}
                                {item.total_tolak > 0 && (
                                  <Chip
                                    label={`Tolak: ${item.total_tolak}`}
                                    color="error"
                                    size="small"
                                  />
                                )}
                                {item.total_perbaiki > 0 && (
                                  <Chip
                                    label={`Perbaiki: ${item.total_perbaiki}`}
                                    color="warning"
                                    size="small"
                                  />
                                )}
                              </MDBox>
                            </MDBox>
                          )}
                          <MDBox mt={2}>
                            <Button
                              variant="contained"
                              color="info"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() =>
                                navigate(
                                  `/task-management/tugas/kinerja-tugas-detail/${item.penerima_id}`,
                                  {
                                    state: {
                                      penerima: item,
                                      tahun: tahun,
                                      bulan: bulan,
                                    },
                                  }
                                )
                              }
                            >
                              Detail
                            </Button>
                          </MDBox>
                        </CardContent>
                      </Card>
                    ))}
                    {kinerjaList.length === 0 && !loading && (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data kinerja
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "No",
                          accessor: "no",
                          width: "5%",
                          Cell: ({ row }) => (
                            <MDTypography variant="caption" color="text">
                              {row.index + 1}
                            </MDTypography>
                          ),
                        },
                        {
                          Header: "Penerima",
                          accessor: "nama_penerima",
                          width: "25%",
                          Cell: ({ row }) => (
                            <MDBox display="flex" alignItems="center" gap={1.5}>
                              <MDAvatar
                                src={formatProfilUrl(
                                  row.original.profil_penerima,
                                  row.original.uid_RSS
                                )}
                                alt={row.original.nama_penerima}
                                size="sm"
                              />
                              <MDTypography
                                variant="button"
                                fontWeight="medium"
                              >
                                {row.original.nama_penerima}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Total Instruksi",
                          accessor: "total_instruksi",
                          width: "10%",
                          Cell: ({ value }) => (
                            <MDTypography variant="body2" fontWeight="bold">
                              {value}
                            </MDTypography>
                          ),
                        },
                        {
                          Header: "Progress Selesai",
                          accessor: "persentase_selesai",
                          width: "20%",
                          Cell: ({ row }) => (
                            <MDBox>
                              <MDProgress
                                value={row.original.persentase_selesai}
                                color="success"
                                variant="gradient"
                                sx={{ mb: 1 }}
                              />
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                {row.original.persentase_selesai}%
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Selesai",
                          accessor: "total_selesai",
                          width: "10%",
                          Cell: ({ value }) => (
                            <Chip
                              label={value}
                              color="success"
                              size="small"
                              sx={{ fontWeight: "bold" }}
                            />
                          ),
                        },
                        {
                          Header: "Proses",
                          accessor: "total_proses",
                          width: "10%",
                          Cell: ({ value }) => (
                            <Chip
                              label={value}
                              color="warning"
                              size="small"
                              sx={{ fontWeight: "bold" }}
                            />
                          ),
                        },
                        {
                          Header: "Belum Dibaca",
                          accessor: "total_belum_dibaca",
                          width: "10%",
                          Cell: ({ value }) => (
                            <Chip
                              label={value}
                              color="error"
                              size="small"
                              sx={{ fontWeight: "bold" }}
                            />
                          ),
                        },
                        {
                          Header: "Rata-rata Rating",
                          accessor: "rata_rata_nilai",
                          width: "15%",
                          Cell: ({ row }) => {
                            const nilai = row.original.rata_rata_nilai;
                            if (nilai === null || nilai === undefined) {
                              return (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  -
                                </MDTypography>
                              );
                            }
                            return (
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                              >
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Icon
                                    key={star}
                                    sx={{
                                      color:
                                        star <= Math.round(nilai)
                                          ? "#FFD700"
                                          : "#E0E0E0",
                                      fontSize: "1.2rem",
                                    }}
                                  >
                                    star
                                  </Icon>
                                ))}
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  ({nilai.toFixed(2)})
                                </MDTypography>
                              </MDBox>
                            );
                          },
                        },
                        {
                          Header: "Respon",
                          accessor: "respon",
                          width: "15%",
                          Cell: ({ row }) => {
                            const {
                              total_respon,
                              total_terima,
                              total_tolak,
                              total_perbaiki,
                            } = row.original;
                            if (total_respon === 0) {
                              return (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  -
                                </MDTypography>
                              );
                            }
                            return (
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                              >
                                {total_terima > 0 && (
                                  <Chip
                                    label={`Terima: ${total_terima}`}
                                    color="success"
                                    size="small"
                                    sx={{ fontSize: "0.7rem" }}
                                  />
                                )}
                                {total_tolak > 0 && (
                                  <Chip
                                    label={`Tolak: ${total_tolak}`}
                                    color="error"
                                    size="small"
                                    sx={{ fontSize: "0.7rem" }}
                                  />
                                )}
                                {total_perbaiki > 0 && (
                                  <Chip
                                    label={`Perbaiki: ${total_perbaiki}`}
                                    color="warning"
                                    size="small"
                                    sx={{ fontSize: "0.7rem" }}
                                  />
                                )}
                              </MDBox>
                            );
                          },
                        },
                        {
                          Header: "Detail",
                          accessor: "detail",
                          width: "8%",
                          Cell: ({ row }) => (
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/task-management/tugas/kinerja-tugas-detail/${row.original.penerima_id}`,
                                  {
                                    state: {
                                      penerima: row.original,
                                      tahun: tahun,
                                      bulan: bulan,
                                    },
                                  }
                                )
                              }
                              title="Lihat Detail"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          ),
                        },
                      ],
                      rows: kinerjaList,
                    }}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default KinerjaTugas;
